import React from 'react';
import { Link } from 'react-router-dom';
import { CheckboxInput } from 'src/shared/design-system/Input/CheckboxInput/CheckboxInput';
import { FormErrorMessage } from 'src/shared/design-system/Input/Error/FormErrorMessage';
import { DualLabeledTextInput } from 'src/shared/design-system/Input/LabeledTextInput/DualLabeledTextInput';
import { LabeledTextInput } from 'src/shared/design-system/Input/LabeledTextInput/LabeledTextInput';
import { Routes } from 'src/shared/Routes';
import styled from 'styled-components';
import { Colors, Font, Spacing } from 'web-common/src/shared/styles';
import { AuthActionButton } from './AuthActionButton';
import { useTermsConsentSentence } from './termsConsentSentence';
import { SignUpWithEmailProps, useSignUpWithEmailForm } from './useSignUpWithEmailForm';

// Negative margin, since we're using gap for spacing
const ErrorMessageWithMargin = styled(FormErrorMessage)`
  margin-top: -${Spacing.HALF};
`;

const TOSText = styled.span`
  > a {
    color: ${Colors.GRAY_DARK_30} !important;
    font-weight: ${Font.Weight.BOLD};
    text-decoration: underline;
  }
`;

export const SignUpWithEmail: React.FC<SignUpWithEmailProps> = (props) => {
  const { email, onEmailChange, requireZipCode, signupCTA } = props;
  const consentSentence = useTermsConsentSentence();
  const {
    firstName,
    onFirstNameChange,
    lastName,
    onLastNameChange,
    zipCode,
    onZipCodeChange,
    displayPhone,
    onPhoneChange,
    password,
    onPasswordChange,
    tosChecked,
    toggleTos,
    emailErrorMessage,
    nameErrorMessage,
    zipCodeErrorMessage,
    passwordErrorMessage,
    phoneErrorMessage,
    signUpErrorMessage,
    loading,
    handleSubmitForm,
    handleKeyDown,
  } = useSignUpWithEmailForm(props);

  return (
    <form onSubmit={handleSubmitForm} onKeyDown={handleKeyDown} data-cy='sign-up-form'>
      <fieldset className='flex flex-col gap-4' disabled={loading}>
        <LabeledTextInput
          label='Email'
          name='email'
          value={email}
          onChange={onEmailChange}
          errorMessage={emailErrorMessage}
          data-cy='sign-up-email'
        />
        <DualLabeledTextInput
          topLabel='First name'
          topName='firstname'
          topValue={firstName}
          onTopChange={onFirstNameChange}
          bottomLabel='Last name'
          bottomName='lastname'
          bottomValue={lastName}
          onBottomChange={onLastNameChange}
          errorMessage={nameErrorMessage}
          autoFocusTop
          top-data-cy='sign-up-first-name'
          bottom-data-cy='sign-up-last-name'
        />
        {requireZipCode && (
          <LabeledTextInput
            label='Zip Code'
            name='zipCode'
            value={zipCode}
            onChange={onZipCodeChange}
            errorMessage={zipCodeErrorMessage}
            data-cy='sign-up-zip-code'
          />
        )}
        <LabeledTextInput
          label='Phone Number'
          name='phone'
          value={displayPhone}
          onChange={onPhoneChange}
          errorMessage={phoneErrorMessage}
          data-cy='sign-up-phone'
        />
        <LabeledTextInput
          password
          label='Password'
          name='password'
          value={password}
          onChange={onPasswordChange}
          errorMessage={passwordErrorMessage}
          data-cy='sign-up-password'
        />
        <CheckboxInput checked={tosChecked} onChange={toggleTos} data-cy='sign-up-tos' gap={Spacing.THREE_QUARTERS}>
          <TOSText>
            {consentSentence}
            <Link to={Routes.CONSUMER_TOS} target='_blank' className='primary'>
              Terms of Service
            </Link>
            {', '}
            <Link to={Routes.CONSUMER_PRIVACY_POLICY} target='_blank' className='primary'>
              Privacy Policy
            </Link>
            , and to receive text messages
          </TOSText>
        </CheckboxInput>
        {signUpErrorMessage ? <ErrorMessageWithMargin>{signUpErrorMessage}</ErrorMessageWithMargin> : null}
        <AuthActionButton value={signupCTA ?? 'Sign up'} disabled={loading} data-cy='sign-up-button' />
      </fieldset>
    </form>
  );
};
